import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { companyAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

export interface DeliveryProduct {
  product_name: string;
  sku: string;
  unit: string;
  total_quantity: number;
}

export interface DeliveryCategory {
  category_name: string;
  total_items: number;
  products: DeliveryProduct[];
}

export interface DeliveryItem {
  product_name: string;
  quantity: number;
  unit: string;
}

export interface DeliveryOrder {
  order_id: number;
  order_number: string;
  customer_name: string;
  customer_phone: string;
  customer_address: string;
  customer_city: string;
  status: string;
  total_amount: number;
  notes: string;
  items: DeliveryItem[];
}

export interface TodaysDeliveryData {
  date: string;
  total_orders: number;
  product_summary: DeliveryCategory[];
  delivery_list: DeliveryOrder[];
}

export const useTodaysDelivery = () => {
  const navigate = useNavigate();
  const { isExpired } = useAuth();
  const [data, setData] = useState<TodaysDeliveryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [deliveringId, setDeliveringId] = useState<number | null>(null);

  const handleMarkDelivered = async (orderId: number) => {
    setDeliveringId(orderId);
    try {
      await companyAPI.updateOrderStatus(orderId, 'delivered');
      setData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          delivery_list: prev.delivery_list.map(o =>
            o.order_id === orderId ? { ...o, status: 'delivered' } : o
          ),
        };
      });
    } catch (err) {
      console.error('Failed to mark as delivered:', err);
    } finally {
      setDeliveringId(null);
    }
  };

  useEffect(() => {
    companyAPI.getTodaysDelivery()
      .then(res => setData(res.data))
      .catch(err => console.error('Failed to load today\'s deliveries:', err))
      .finally(() => setLoading(false));
  }, []);

  const fmt = (n: number) => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2 });

  const statusColor: Record<string, string> = {
    pending: '#f59e0b', confirmed: '#3b82f6', processing: '#8b5cf6',
    shipped: '#06b6d4', delivered: '#10b981',
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  const deliveredCount = data ? data.delivery_list.filter(o => o.status === 'delivered').length : 0;

  const navigateToDashboard = () => navigate('/company/dashboard');

  return {
    data,
    loading,
    deliveringId,
    isExpired,
    handleMarkDelivered,
    fmt,
    statusColor,
    formatDate,
    deliveredCount,
    navigateToDashboard,
  };
};
